// Visit reminder notification utilities

const REMINDER_KEY = 'dealer_reminders_v1';
const REMINDER_DAYS = 30; // Remind after 30 days without visit

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return { granted: false, reason: 'not_supported' };
  }

  if (Notification.permission === 'granted') {
    return { granted: true };
  }

  if (Notification.permission === 'denied') {
    return { granted: false, reason: 'denied' };
  }

  const permission = await Notification.requestPermission();
  return { granted: permission === 'granted', reason: permission };
}

// Check if notifications are enabled
export function areNotificationsEnabled() {
  return 'Notification' in window && Notification.permission === 'granted';
}

// Get current notification permission status
export function getNotificationPermissionStatus() {
  if (!('Notification' in window)) {
    return 'not_supported';
  }
  return Notification.permission; // 'default', 'granted', or 'denied'
}

// Get stores that need revisit (> 30 days since last visit)
export function getStoresNeedingRevisit(lojas, days = REMINDER_DAYS) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return lojas.filter(loja => {
    if (!loja.visit) return true; // Never visited
    
    const visitDate = new Date(loja.visit);
    visitDate.setHours(0, 0, 0, 0);
    
    const diffTime = today - visitDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= days;
  }).map(loja => ({
    ...loja,
    daysSinceVisit: loja.visit 
      ? Math.ceil((today - new Date(loja.visit)) / (1000 * 60 * 60 * 24))
      : null
  }));
}

// Show a local notification
export function showNotification(title, options = {}) {
  if (!areNotificationsEnabled()) {
    console.log('Notifications not enabled');
    return null;
  }

  const notification = new Notification(title, {
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192.png',
    tag: 'dealer-manager-reminder',
    renotify: true,
    ...options
  });

  notification.onclick = () => {
    window.focus();
    notification.close();
  };

  return notification;
}

// Check and show reminders for overdue stores
export function checkAndShowReminders(lojas) {
  const overdueStores = getStoresNeedingRevisit(lojas);
  
  if (overdueStores.length === 0) return;

  // Get last reminder time
  const lastReminder = localStorage.getItem(REMINDER_KEY);
  const now = Date.now();
  
  // Only show reminder once per day
  if (lastReminder && (now - parseInt(lastReminder)) < 24 * 60 * 60 * 1000) {
    return;
  }

  // Show notification
  if (overdueStores.length === 1) {
    showNotification('Visit Reminder', {
      body: `${overdueStores[0].name} hasn't been visited in ${overdueStores[0].daysSinceVisit || 'many'} days.`
    });
  } else {
    showNotification('Visit Reminders', {
      body: `${overdueStores.length} stores need a visit! Some haven't been visited in over ${REMINDER_DAYS} days.`
    });
  }

  // Save reminder time
  localStorage.setItem(REMINDER_KEY, now.toString());
}

// Schedule periodic reminder check
export function scheduleReminderCheck(lojas) {
  // Check immediately
  setTimeout(() => checkAndShowReminders(lojas), 5000);
  
  // Check every hour while app is open
  const intervalId = setInterval(() => checkAndShowReminders(lojas), 60 * 60 * 1000);
  
  return () => clearInterval(intervalId);
}

// Get reminder settings from localStorage
export function getReminderSettings() {
  const settings = localStorage.getItem('dealer_reminder_settings_v1');
  return settings ? JSON.parse(settings) : {
    enabled: true,
    days: REMINDER_DAYS
  };
}

// Save reminder settings
export function saveReminderSettings(settings) {
  localStorage.setItem('dealer_reminder_settings_v1', JSON.stringify(settings));
}
